import { useCallback } from 'react';
import {
  ConvertAllocationsProps,
  DeleteAllocationsProps,
  FetchableResourceType,
  ResourceType,
} from 'api/resource-management/types';
import { useAllocationAndSummaryManager } from '../resource-summary-manager';
import { ManagerPropsWithLoadedRange } from '../types';

interface ReturnProps {
  deleteAllocationsForResource: (params: DeleteAllocationsProps) => void;
  convertAllocationsForResource: (params: ConvertAllocationsProps) => void;
  deleteAllocationHandler: (params: DeleteAllocationsProps) => void;
  convertAllocationHandler: (params: ConvertAllocationsProps) => void;
}

export const useAllocationsMoreActionsManager = (
  props: ManagerPropsWithLoadedRange,
  removeAllocationsForResource: (params: DeleteAllocationsProps) => void,
): ReturnProps => {
  const { schedulerRef, rangeLoadedInUI } = props;

  const { updateAllocationAndSummary } = useAllocationAndSummaryManager(props);

  const deleteAllocationsForResource = useCallback(
    (params: DeleteAllocationsProps) => {
      schedulerRef.current.instance.suspendRefresh();

      removeAllocationsForResource(params);
      updateAllocationAndSummary(
        {
          [FetchableResourceType.ProjectList]: [params.projectId],
          ...(params.resourceIdentifier.type === ResourceType.User
            ? {
                [FetchableResourceType.UserList]: [
                  params.resourceIdentifier.userId,
                ],
              }
            : {
                [FetchableResourceType.RoleList]: [
                  params.resourceIdentifier.roleId,
                ],
              }),
        },
        rangeLoadedInUI,
      );

      schedulerRef.current.instance.resumeRefresh(true);
    },
    [
      rangeLoadedInUI,
      removeAllocationsForResource,
      schedulerRef,
      updateAllocationAndSummary,
    ],
  );

  const convertAllocationsForResource = useCallback(
    ({ projectId, resourceIdentifier }: ConvertAllocationsProps) => {
      schedulerRef.current.instance.suspendRefresh();

      updateAllocationAndSummary(
        {
          [FetchableResourceType.ProjectList]: [projectId],
          ...(resourceIdentifier != null
            ? {
                ...(resourceIdentifier.type === ResourceType.User
                  ? {
                      [FetchableResourceType.UserList]: [
                        resourceIdentifier.userId,
                      ],
                    }
                  : {
                      [FetchableResourceType.RoleList]: [
                        resourceIdentifier.roleId,
                      ],
                    }),
              }
            : {}),
        },
        rangeLoadedInUI,
      );

      schedulerRef.current.instance.resumeRefresh(true);
    },
    [rangeLoadedInUI, schedulerRef, updateAllocationAndSummary],
  );

  const deleteAllocationHandler = useCallback(
    (params: DeleteAllocationsProps) => {
      schedulerRef.current.instance.trigger('onDeleteAllocations', params);
    },
    [schedulerRef],
  );

  const convertAllocationHandler = useCallback(
    (params: ConvertAllocationsProps) => {
      schedulerRef.current.instance.trigger('onConvertAllocations', params);
    },
    [schedulerRef],
  );

  return {
    deleteAllocationsForResource,
    convertAllocationsForResource,
    deleteAllocationHandler,
    convertAllocationHandler,
  };
};
